"use client";

import { CallStatus } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { MouseEvent, useEffect, useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconPlay from "@/app/(components)/generics/Icons/IconPlay";
import IconRestart from "@/app/(components)/generics/Icons/IconRestart";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import {
  useBrand,
  useCanRunPaidActions,
  useCanSpendCredits,
  useIsCompanyOwner,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { runCallAnalysis } from "@/app/actions/call/run-analysis/action";
import { InsufficientBalanceError } from "@/app/errors/InsufficientBalanceError";
import { useFetchCalls } from "@/app/hooks/fetchs/calls/useFetchCalls";
import { useCreditGuard } from "@/app/hooks/useCreditGuard";
import { ModalStore } from "@/app/stores/ModalStore";
import { OfferStore } from "@/app/stores/OfferStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { UserStore } from "@/app/stores/UserStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { CreditBucket } from "@/app/types/billing/CreditWallet";
import { CallFile } from "@/app/types/call/CallFile";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { ULogger } from "@/app/utils/ULogger";
import { UTime } from "@/app/utils/UTime";
import { UWallet } from "@/app/utils/UWallet";
import { creditsForAddedDuration } from "@/app/vars/Credits";
import { handleAnalysisStartedOptimistic } from "../../CallAnalysis.logic";
import styles from "./RunAnalysis.module.css";

export enum RunAnalysisMode {
  ICON = "ICON",
  FULL = "FULL",
  MENU = "MENU",
}

type Props = DefaultProps & {
  callId: number;
  onStart?: (newStatus: CallStatus) => void;
  mode?: RunAnalysisMode;
  callFile: CallFile;
};

export default function RunAnalysis(props: Props) {
  const T = useT();
  const { params, onStart, mode = RunAnalysisMode.ICON, callFile } = props;
  const user = useAuthUser();
  const decrementCredits = UserStore((state) => state.decrementCredits);
  const guardCredits = useCreditGuard(params);
  const isTenant = useBrand() !== null;
  const canSpendCredits = useCanSpendCredits();
  const isCompanyOwner = useIsCompanyOwner();
  const balance = UserStore((state) =>
    state.user ? UWallet.spendable(state.user, CreditBucket.CALL) : 0,
  );
  // Viewers (members / unshared admins on a subdomain) can't run: hide the button entirely.
  const canRunPaidActions = useCanRunPaidActions();
  const queryClient = useQueryClient();

  const project = ProjectStore((state) => state.currentProject);
  const currentOfferId = OfferStore((state) => state.currentOfferId);
  const { data: callsData } = useFetchCalls({
    projectId: project?.id,
    offerId: currentOfferId,
    enabled: !!project?.id,
    isCallAnalysisPage: true,
  });

  const liveCall = callsData?.calls.find((c) => c.id === (callFile.id ?? callFile.savedCall?.id));
  const currentStatus = liveCall?.status ?? callFile.status;

  const [isStarting, setIsStarting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (
      hasStarted &&
      (currentStatus === CallStatus.ANALYZED || currentStatus === CallStatus.FAILED)
    ) {
      setHasStarted(false);
    }
  }, [currentStatus, hasStarted]);

  const runNow = async (callId: number, cost: number, durationSeconds: number) => {
    setIsStarting(true);

    try {
      await runCallAnalysis(callId);

      handleAnalysisStartedOptimistic(queryClient, callId, project?.id);
      decrementCredits(cost, CreditBucket.CALL);
      if (project?.id) {
        ProjectStore.getState().addBilledCallSeconds(project.id, durationSeconds);
      }

      setHasStarted(true);

      if (onStart) {
        onStart(CallStatus.ANALYZING);
      }
    } catch (error) {
      ULogger.error("[RunAnalysis] Failed to start analysis:", error);

      if (error instanceof InsufficientBalanceError) {
        ModalStore.getState().openBilling(params);
      }
    } finally {
      setIsStarting(false);
    }
  };

  const handleStart = (e?: MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!user || isStarting || hasStarted) return;

    const callId = callFile.id ?? callFile.savedCall?.id;
    if (!callId) return;

    if (isTenant && !isCompanyOwner && (!canSpendCredits || balance <= 0)) {
      ModalStore.getState().openBilling(params);
      return;
    }

    const duration = liveCall?.duration ?? callFile.savedCall?.duration ?? callFile.duration;
    const durationSeconds = duration ?? 0;
    const billedBefore = project?.callsBilledSeconds ?? 0;
    const cost = creditsForAddedDuration(billedBefore, durationSeconds);

    if (!guardCredits(cost, CreditBucket.CALL)) {
      return;
    }

    const isIncluded = cost === 0;

    ModalStore.getState().openModal(
      Components.CostConfirm,
      T(TKey.CALL_ANALYSIS_RUN_CONFIRM_TITLE) as string,
      {
        params,
        cost: isIncluded
          ? (T(TKey.CALL_ANALYSIS_RUN_CONFIRM_INCLUDED) as string)
          : `${UWallet.hoursFromCredits(cost)} ${T(TKey.CREDITS_HOURS_SHORT)}`,
        ctaText: T(TKey.CALL_ANALYSIS_RUN_CONFIRM_CTA) as string,
        detail: duration ? UTime.formatSecondsShort(duration) : undefined,
        note: T(
          isIncluded
            ? TKey.CALL_ANALYSIS_RUN_CONFIRM_NOTE_INCLUDED
            : TKey.CALL_ANALYSIS_RUN_CONFIRM_NOTE,
        ) as string,
        cumulativeSeconds: billedBefore,
        onConfirm: () => runNow(callId, cost, durationSeconds),
      },
      ModalType.SMALL,
    );
  };

  if (
    !canRunPaidActions ||
    isStarting ||
    hasStarted ||
    !currentStatus ||
    currentStatus === CallStatus.ANALYZING
  ) {
    return null;
  }

  const isRestart = currentStatus === CallStatus.ANALYZED || currentStatus === CallStatus.FAILED;
  const Icon = isRestart ? IconRestart : IconPlay;

  if (currentStatus === CallStatus.ANALYZED) {
    return null;
  }

  const iconColor = "var(--color-green-3)";

  if (mode === RunAnalysisMode.MENU) {
    return (
      <li>
        <button className={styles.menuItem} onClick={handleStart}>
          <span className={styles.menuItemIcon}>
            <Icon size={16} />
          </span>
          <span>
            {isRestart
              ? (T(TKey.CALL_DETAIL_ANALYSIS_RERUN) as string)
              : (T(TKey.CALL_DETAIL_ANALYSIS_RUN) as string)}
          </span>
        </button>
      </li>
    );
  }

  if (mode === RunAnalysisMode.ICON) {
    return (
      <Tooltip
        content={isRestart ? T(TKey.CALL_DETAIL_ANALYSIS_RERUN) : T(TKey.CALL_DETAIL_ANALYSIS_RUN)}
        position={TooltipPosition.BOTTOM_LEFT}
        type={TooltipType.GREEN}
        trigger={
          <Button
            params={params}
            isLink={false}
            type={ButtonType.GHOST}
            iconOnly
            min
            beforeIcon={
              <span style={{ display: "flex", color: iconColor }}>
                <Icon size={18} />
              </span>
            }
            className={styles.playButton}
            onClick={handleStart}
          />
        }
      />
    );
  }

  const fullIconColor = isRestart ? iconColor : "var(--color-white-1)";

  return (
    <Button
      params={params}
      isLink={false}
      type={isRestart ? ButtonType.OUTLINE : ButtonType.PRIMARY}
      min={true}
      beforeIcon={
        <span style={{ display: "flex", color: fullIconColor }}>
          <Icon size={18} />
        </span>
      }
      text={
        isRestart
          ? (T(TKey.CALL_DETAIL_ANALYSIS_RERUN) as string)
          : (T(TKey.CALL_DETAIL_ANALYSIS_RUN) as string)
      }
      onClick={handleStart}
    />
  );
}
